import type {
  AddressAttributes,
  AddressAutocomplete
} from '@library/types/addressAutocomplete';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@library/components/buttons';
import { Input } from '@library/components/inputs';
import type { Ref } from 'react';
import { cn } from '@library/component-utils/cn';

export interface AddressAutocompleteInputProps {
  className?: string;
  onSelectedAddress: (address: AddressAttributes) => void;
  onValueChange: (value: string) => void;
  placeholder?: string;
  validAddresses?: Array<AddressAutocomplete>;
  value: string;
}

const AddressInput = (
  {
    className = '',
    onSelectedAddress,
    onValueChange,
    placeholder = 'Search for address',
    validAddresses = [],
    value,
    ...rest
  }: AddressAutocompleteInputProps,
  ref: Ref<HTMLInputElement>
) => {
  const [showResults, setShowResults] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Hide dropdown when clicking outside
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setShowResults(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleValueChange = useCallback(
    (newValue: string) => {
      onValueChange(newValue);
      setShowResults(!!newValue);
    },
    [onValueChange]
  );

  const handleSelectedAddress = useCallback(
    (address: AddressAttributes) => () => {
      onSelectedAddress(address);
      setShowResults(false);
    },
    [onSelectedAddress]
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <Input
        autoComplete="off"
        className={cn(
          'text-soClean-100 placeholder:text-soClean-180 border-soFresh-140 bg-soFresh-110 w-full rounded-md border text-sm hover:ring-0 focus:ring-0 sm:leading-6',
          className
        )}
        onValueChange={handleValueChange}
        placeholder={placeholder}
        ref={ref}
        type="search"
        value={value}
        {...rest}
      />
      {showResults && !!validAddresses.length && (
        <div className="bg-soFresh-130 absolute z-10 mt-1 w-full rounded-md">
          {validAddresses.map(({ attributes, id }) => (
            <Button
              key={id}
              className="text-soClean-100 hover:bg-soFresh-110 bg-soFresh-130 w-full cursor-pointer rounded-md p-3 text-left text-sm"
              onClick={handleSelectedAddress(attributes)}
            >
              {attributes.formatted_address}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export const AddressAutocompleteInput = forwardRef(AddressInput);
